import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { ProductCard } from '@theme/product-card';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';
const IMAGE_VIEW_EVENT = 'catalog:image-view-change';
let preferredImageView = 'product';
try {
  if (sessionStorage.getItem('catalog-image-view') === 'model') preferredImageView = 'model';
} catch {
  // The current page still works when storage is unavailable.
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #gridObserver = new MutationObserver(() => {
    this.#syncProductCardImageSizes();
    this.#syncImageView();
  });

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(IMAGE_VIEW_EVENT, this.#syncImageView);
    this.#observeGrid();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGrid();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(IMAGE_VIEW_EVENT, this.#syncImageView);
    this.#gridObserver.disconnect();
    this.#defaultImageSizes = new WeakMap();
  }

  /** @param {Event} event - Native checkbox change. */
  updateImageView({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    preferredImageView = target.checked ? 'model' : 'product';
    try {
      sessionStorage.setItem('catalog-image-view', preferredImageView);
    } catch {
      // Keep the in-memory preference for this visit.
    }
    document.dispatchEvent(new Event(IMAGE_VIEW_EVENT));
  }

  #syncImageView = () => {
    const controls = this.querySelectorAll('[data-card-image-control]');
    if (!controls.length) return;
    const cards = this.refs.grid?.querySelectorAll('product-card') ?? [];
    let available = false;
    for (const card of cards) {
      if (!(card instanceof ProductCard)) continue;
      available ||= card.hasImageView;
      card.setImageView(preferredImageView);
    }
    for (const control of controls) {
      control.hidden = !available;
      const input = control.querySelector('input');
      if (input) input.checked = preferredImageView === 'model';
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncProductCardImageSizes();
  }

  #observeGrid() {
    this.#gridObserver.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
    this.#syncProductCardImageSizes();
    this.#syncImageView();
  }

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const layout = grid.getAttribute('product-grid-view');
    const mobileLayout = !mediaQueryLarge.matches ? layout : null;
    const desktopZoomIn = mediaQueryLarge.matches && layout === 'zoom-in';
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    for (const image of images) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);

      const sizes = desktopZoomIn
        ? '33vw'
        : mobileLayout === 'mobile-single'
          ? '100vw'
          : mobileLayout === 'mobile-dense'
            ? '33vw'
            : this.#defaultImageSizes.get(image);
      if (sizes !== undefined && image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}


